import React, { forwardRef } from "react";
import { cn } from "../../lib/utils.js";
import { useRovingTabIndex } from "../../primitives/index.js";
import { TabsContext, panelId, tabId } from "./tabs-context.js";
import {
  tabsToneClasses,
  type DeprecatedTabsTone,
  type TabsTone,
} from "./tabs-tones.js";
import { useTabsIndicator } from "./useTabsIndicator.js";

export { TabPanel, type TabPanelProps } from "./TabPanel.js";

/* ---------------- Tabs (C8 #5 — content tabs) ----------------
   The APG "Tabs" pattern (strategy §13.2, §15): a tablist of tabs that each
   control a tabpanel. Distinct from the shell `TabStrip` (browser-chrome tabs)
   and the compact `SegmentedControl`; this is the content-tabs gap the Replit
   audit surfaced (33 files hand-roll role="tab" + aria-controls panels).

   Curated, items-driven API — consistent with RadioGroup/Select/SegmentedControl
   rather than a free-form <Tab> compound: `Tabs` renders the tablist from
   `items` and owns roving + selection; `TabPanel` children match by `value`.

   a11y: role=tablist/tab/tabpanel, aria-selected, tab aria-controls=panel,
   panel aria-labelledby=tab. Panels register themselves on mount, and a tab
   only gets aria-controls while its panel is in the DOM — inactive panels
   unmount by default, and a dangling reference is an a11y defect. Roving tabindex (useRovingTabIndex): Arrow keys
   (Left/Right horizontal, Up/Down vertical) + Home/End move focus; the focused
   tab is the only one in the tab sequence. Activation:
   • "automatic" (default) — focus selects (panel follows arrow keys).
   • "manual" — focus moves; Enter/Space selects.
   Focus only moves on keyboard nav, never when selection syncs from outside
   (the TabStrip navigatingRef pattern), so Tabs never steals focus on mount. */

export interface TabItem {
  value: string;
  label: React.ReactNode;
  icon?: React.ElementType;
  disabled?: boolean;
}

export type TabsSize = "md" | "lg";

/* Literal class strings so Tailwind sees them; md is today's h-9. */
const TRIGGER_HEIGHT: Record<TabsSize, string> = { md: "h-9", lg: "h-11" };

export interface TabsProps extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  "onChange"
> {
  items: TabItem[];
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  /**
   * Active-tab underline. Omitted, it is the teal selection (C7, d3).
   * `neutral` reads lib/tones.ts; `ai` is the Player AI iris (ADR-0076).
   * `teal`, `iris` and `accent` are deprecated aliases: `teal` is the default,
   * `iris` is `ai`, and `accent` keeps the old accent underline.
   */
  tone?: TabsTone | DeprecatedTabsTone;
  /** Trigger height on the control ramp: `md` 36px (default), `lg` 44px. */
  size?: TabsSize;
  /**
   * Pads a horizontal tablist in by 20px at the sides and 4px on top, so the
   * tabs line up with a dialog header; its bottom rule still runs full width.
   * Pair it with `TabPanel padding="inset"`.
   */
  inset?: boolean;
  /**
   * The tablist keeps its height and the panel area takes the rest, as a
   * column that clips (a TabPanel inside scrolls with a `flex-1 min-h-0
   * overflow-y-auto` className). Give the Tabs itself a height: a `flex-1
   * min-h-0` or `h-full` className.
   */
  fill?: boolean;
  /** "automatic": focus selects. "manual": Enter/Space selects. Default automatic. */
  activation?: "automatic" | "manual";
  orientation?: "horizontal" | "vertical";
  /** Accessible name for the tablist. */
  "aria-label"?: string;
  "aria-labelledby"?: string;
  /** TabPanel children. */
  children?: React.ReactNode;
}

const TabsRoot = forwardRef<HTMLDivElement, TabsProps>(function Tabs(
  {
    items,
    value,
    defaultValue,
    onValueChange,
    tone,
    size = "md",
    inset = false,
    fill = false,
    activation = "automatic",
    orientation = "horizontal",
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledby,
    className,
    children,
    ...rest
  },
  ref,
) {
  const autoId = React.useId();
  const baseId = autoId;
  const isControlled = value !== undefined;
  const [internal, setInternal] = React.useState(
    defaultValue ??
      items.find((i) => !i.disabled)?.value ??
      items[0]?.value ??
      "",
  );
  const selected = isControlled ? (value as string) : internal;

  const select = React.useCallback(
    (v: string) => {
      const item = items.find((i) => i.value === v);
      if (!item || item.disabled) return;
      if (!isControlled) setInternal(v);
      onValueChange?.(v);
    },
    [items, isControlled, onValueChange],
  );

  const [mountedPanels, setMountedPanels] = React.useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const registerPanel = React.useCallback((v: string) => {
    setMountedPanels((prev) => new Set(prev).add(v));
    return () => {
      setMountedPanels((prev) => {
        const next = new Set(prev);
        next.delete(v);
        return next;
      });
    };
  }, []);
  const ctx = React.useMemo(
    () => ({ value: selected, baseId, registerPanel }),
    [selected, baseId, registerPanel],
  );

  const tabRefs = React.useRef<Array<HTMLButtonElement | null>>([]);
  const navigatingRef = React.useRef(false);
  const selectedItemIndex = items.findIndex((i) => i.value === selected);
  const selectedIndex = Math.max(0, selectedItemIndex);

  const onActiveChange = React.useCallback(
    (i: number) => {
      if (!navigatingRef.current) return;
      tabRefs.current[i]?.focus();
      if (activation === "automatic") select(items[i]?.value ?? "");
    },
    [activation, items, select],
  );

  const {
    activeIndex,
    setActiveIndex,
    onKeyDown: rovingKeyDown,
  } = useRovingTabIndex({
    itemCount: items.length,
    orientation,
    loop: true,
    initialIndex: selectedIndex,
    onActiveChange,
  });

  // Keep the roving anchor on the selected tab when selection changes from
  // outside; navigatingRef is false here, so this never moves DOM focus.
  React.useEffect(() => {
    setActiveIndex(selectedIndex);
  }, [selectedIndex, setActiveIndex]);

  const vertical = orientation === "vertical";
  const toneClasses = tabsToneClasses(tone);
  const { tablistRef, indicator } = useTabsIndicator({
    vertical,
    selectedItemIndex,
    items,
    tabRefs,
  });

  return (
    <div
      ref={ref}
      className={cn(vertical ? "flex gap-4" : "flex flex-col", className)}
      {...rest}
    >
      <div
        ref={tablistRef}
        role="tablist"
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledby}
        aria-orientation={orientation}
        /* The horizontal tablist WRAPS rather than clipping (PB-5332 review
           D1). Triggers are `whitespace-nowrap`, so four of them overflow a
           390px viewport and the fourth was cut off at the edge — a navigation
           target you could see a sliver of and not read.

           Wrapping rather than horizontal scrolling, deliberately. Every
           consumer tops out at FOUR tabs (Identity, Members and Communication
           admin), so the worst case is two short rows with every label fully
           visible — no scrollbar, no off-screen target, and nothing needing a
           scroll affordance to discover. A scroll container would have kept
           the fourth tab hidden until someone thought to swipe.

           Inert when the tabs fit: `flex-wrap` only wraps once a row overflows,
           so desktop rendering is unchanged.

           NOT this page's bug alone — Members and Communication admin carry
           four tabs through this same component and clipped identically. */
        className={cn(
          "flex",
          vertical
            ? "flex-col gap-1 border-e border-pb-border pe-2"
            : "relative flex-wrap items-center gap-1 border-b border-pb-border",
          // Padding sits inside the rule, so the rule stays full width.
          inset && !vertical && "px-5 pt-1",
          fill && "shrink-0",
        )}
      >
        {!vertical && (
          <span
            aria-hidden
            data-tab-indicator
            className={cn(
              "pointer-events-none absolute -bottom-px h-0.5 hc:h-0.75",
              /* The shared slider clock (ADR-0091) rather than this one
                 component's private copy of it: transform/width on the state
                 duration and the standard ease live in `.pb-slider-indicator`,
                 so a tab underline and a segmented control's indicator travel
                 at the same speed instead of agreeing by coincidence. */
              "pb-slider-indicator motion-reduce:transition-none",
              toneClasses.indicator,
              indicator ? "opacity-100" : "opacity-0",
            )}
            // `offsetLeft` is physical, so the anchor is the physical left
            // edge too — the same maths holds in RTL. The Y term lifts the
            // span to the active tab's row when the tablist has wrapped.
            style={{
              left: 0,
              transform: `translate(${indicator?.left ?? 0}px, ${-(indicator?.rise ?? 0)}px)`,
              width: indicator?.width ?? 0,
            }}
          />
        )}
        {items.map((item, i) => {
          const isActive = item.value === selected;
          const Icon = item.icon;
          return (
            <button
              key={item.value}
              type="button"
              role="tab"
              id={tabId(baseId, item.value)}
              ref={(node) => {
                tabRefs.current[i] = node;
              }}
              aria-selected={isActive}
              aria-controls={
                mountedPanels.has(item.value)
                  ? panelId(baseId, item.value)
                  : undefined
              }
              tabIndex={i === activeIndex ? 0 : -1}
              disabled={item.disabled}
              onClick={() => {
                setActiveIndex(i);
                select(item.value);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  select(item.value);
                  return;
                }
                navigatingRef.current = true;
                rovingKeyDown(e);
                navigatingRef.current = false;
              }}
              className={cn(
                /* `shrink-0` so a trigger keeps its label instead of being
                   squeezed narrower than its text — without it the flex items
                   absorb the shortfall, the row never overflows, and it
                   therefore never WRAPS; the labels just crush instead. */
                `inline-flex shrink-0 items-center gap-1.5 px-3 ${TRIGGER_HEIGHT[size]} text-sm font-medium whitespace-nowrap transition-colors`,
                "focus-ring rounded-t-md",
                "disabled:opacity-50 disabled:cursor-not-allowed",
                // The indicator thickens to 3px in high contrast (PB-5274).
                // Both states carry the width, not just the active one, or the
                // row would shift a pixel as the selection moves. Horizontal
                // tabs keep the edge transparent: the sliding indicator above
                // paints the selection.
                vertical
                  ? "-me-px border-e-2 hc:border-e-[3px] justify-start"
                  : "-mb-px border-b-2 hc:border-b-[3px]",
                isActive
                  ? cn(
                      "text-pb-text",
                      vertical ? toneClasses.edge : "border-transparent",
                    )
                  : "border-transparent text-pb-text-secondary hover:text-pb-text active:text-pb-text",
              )}
            >
              {Icon && <Icon className="w-4 h-4 shrink-0" />}
              {item.label}
            </button>
          );
        })}
      </div>
      <TabsContext.Provider value={ctx}>
        <div
          className={cn(
            vertical ? "flex-1 min-w-0" : "",
            fill && "flex min-h-0 flex-1 flex-col overflow-hidden",
          )}
        >
          {children}
        </div>
      </TabsContext.Provider>
    </div>
  );
});

type WithRef<P> = P & React.RefAttributes<HTMLDivElement>;

/* Overloads exist only to carry the deprecation below (Alert's pattern); the
   last one is the whole API, so every other call resolves to it unchanged. */
export interface TabsComponent {
  (props: WithRef<TabsProps & { tone?: TabsTone }>): React.ReactNode;
  /**
   * @deprecated `tone="iris"` is `tone="ai"`. `tone="teal"` and
   * `tone="accent"` are raw colour names: `teal` is the default selection
   * (omit `tone`) and `accent` keeps the old accent underline. Removed in the
   * release after the adoption PR (ADR-0092 amendment).
   */
  (props: WithRef<TabsProps & { tone: DeprecatedTabsTone }>): React.ReactNode;
  (props: WithRef<TabsProps>): React.ReactNode;
  readonly $$typeof: symbol;
  displayName?: string | undefined;
}

export const Tabs: TabsComponent = TabsRoot;
